import React, { useEffect, useState } from 'react';
import axios from 'axios';

const LANG_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'es', label: 'Spanish' },
];

const DEFAULT_PROFILE = {
  name: '',
  address: '',
  taxId: '',
  industryType: '',
  phone: '',
  website: '',
  email: '',
  hours: '',
  behaviorPrompt: '',
  priorityLanguage: 'en', // default
};

const CompanyProfileForm = () => {
  const [formData, setFormData] = useState(DEFAULT_PROFILE);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileMessage, setProfileMessage] = useState('');

  // 🔧 API base sem nada chumbado:
  // 1) window.__API_URL__ (injetado pelo backend se existir)
  // 2) REACT_APP_API_URL (definido no .env do front)
  // 3) fallback '' -> usa mesma origin com rota relativa
  const API_URL =
    (typeof window !== 'undefined' && window.__API_URL__) ||
    process.env.REACT_APP_API_URL ||
    '';

  // 🔐 monta headers com token do login (sem nome chumbado fixo)
  const getAuthHeaders = () => {
    if (typeof window === 'undefined') return {};

    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('jwt') ||
      localStorage.getItem('access_token');

    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  };

  /* =========================
     LOAD COMPANY PROFILE
  ========================== */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const headers = getAuthHeaders();

        // Se não tiver token, não tenta carregar (evita erro 401 à toa)
        if (!headers.Authorization) {
          setLoadingProfile(false);
          return;
        }

        const { data } = await axios.get(`${API_URL}/company-profile`, {
          headers,
        });

        if (cancelled) return;

        const priorityLanguage =
          (data && (data.priorityLanguage || data.priority_language)) || 'en';

        setFormData({
          ...DEFAULT_PROFILE,
          ...(data || {}),
          priorityLanguage,
        });
      } catch (err) {
        console.error(
          'Error loading company profile:',
          err?.response?.data || err.message
        );
        setProfileMessage('Error loading company profile.');
      } finally {
        if (!cancelled) setLoadingProfile(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [API_URL]);

  /* =========================
     PROFILE HANDLERS
  ========================== */
  const handleChangeProfile = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitProfile = async (e) => {
    e.preventDefault();
    setProfileMessage('');

    try {
      const headers = {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      };

      if (!headers.Authorization) {
        setProfileMessage('❌ Not authorized. Please log in again.');
        return;
      }

      await axios.post(`${API_URL}/company-profile`, formData, { headers });
      setProfileMessage('✅ Profile saved successfully.');
    } catch (err) {
      console.error(
        'Error saving profile:',
        err?.response?.data || err.message
      );
      if (err?.response?.status === 401) {
        setProfileMessage('❌ Not authorized. Please log in again.');
      } else {
        setProfileMessage('❌ Error saving profile.');
      }
    }
  };

  if (loadingProfile) return <p>Loading...</p>;

  return (
    <div className="max-w-xl">
      {profileMessage && (
        <div className="mb-4 text-sm font-medium">
          {profileMessage}
        </div>
      )}

      {/* PROFILE FORM ============================================ */}
      <form
        onSubmit={handleSubmitProfile}
        className="space-y-4 mb-8"
      >
        <div>
          <label className="block text-sm font-medium mb-1">
            Company Name
          </label>
          <input
            type="text"
            name="name"
            required
            value={formData.name}
            onChange={handleChangeProfile}
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Address
          </label>
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChangeProfile}
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Tax ID
          </label>
          <input
            type="text"
            name="taxId"
            value={formData.taxId}
            onChange={handleChangeProfile}
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Business Type
          </label>
          <input
            type="text"
            name="industryType"
            required
            value={formData.industryType}
            onChange={handleChangeProfile}
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Phone
          </label>
          <input
            type="text"
            name="phone"
            value={formData.phone}
            onChange={handleChangeProfile}
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Website
          </label>
          <input
            type="text"
            name="website"
            value={formData.website}
            onChange={handleChangeProfile}
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChangeProfile}
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Business Hours
          </label>
          <input
            type="text"
            name="hours"
            value={formData.hours}
            onChange={handleChangeProfile}
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            AI Behavior Instructions
          </label>
          <textarea
            name="behaviorPrompt"
            rows={6}
            value={formData.behaviorPrompt}
            onChange={handleChangeProfile}
            className="w-full border rounded px-3 py-2 text-sm"
            placeholder="Define how the AI should behave (tone, style, etc.)"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Priority Language
          </label>
          <select
            name="priorityLanguage"
            value={formData.priorityLanguage}
            onChange={handleChangeProfile}
            className="w-full border rounded px-3 py-2 text-sm bg-white"
          >
            {LANG_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Used as fallback on greetings/ambiguous texts. If the
            user writes in another language, the assistant will
            continue in the detected language.
          </p>
        </div>

        <div>
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyProfileForm;
